import type { Envelope, ErrorCode } from "@shared/envelope";
import { isTransient } from "@shared/envelope";

/** Every failure application code sees. Branch on `code`, never on `status`. */
export class ApiError extends Error {
	constructor(
		readonly code: ErrorCode,
		readonly status: number,
		message: string,
		readonly details?: Record<string, unknown>,
		readonly requestId?: string,
	) {
		super(message);
		this.name = "ApiError";
	}
	get transient(): boolean {
		return isTransient(this.code);
	}
	get fieldErrors(): Record<string, string[]> {
		return (this.details?.["field_errors"] as Record<string, string[]> | undefined) ?? {};
	}
}

const transportError = (status: number) =>
	new ApiError("SERVICE_UNAVAILABLE", status, "Can't reach the server right now. Please try again.");

interface Options {
	method?: "GET" | "POST" | "PATCH" | "DELETE";
	body?: unknown;
	signal?: AbortSignal;
}

async function request<T>(path: string, { method = "GET", body, signal }: Options = {}): Promise<T> {
	let response: Response;
	try {
		response = await fetch(`/api${path}`, {
			method,
			headers: body === undefined ? {} : { "Content-Type": "application/json" },
			body: body === undefined ? undefined : JSON.stringify(body),
			signal,
		});
	} catch (err) {
		if (err instanceof DOMException && err.name === "AbortError") throw err;
		throw transportError(0);
	}
	if (response.status === 204) return null as T;

	let envelope: Envelope<T>;
	try {
		envelope = (await response.json()) as Envelope<T>;
	} catch {
		throw transportError(response.status);
	}
	if (envelope.success) return envelope.data;
	const { code, message, details } = envelope.error;
	throw new ApiError(code, response.status, message, details, envelope.request_id);
}

export const api = {
	get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
	post: <T>(path: string, body: unknown) => request<T>(path, { method: "POST", body }),
	patch: <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body }),
	delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export const errorMessage = (err: unknown): string =>
	err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Something went wrong.";
